import { useState } from "react";
import { toast } from "sonner";
import { api } from "../../../api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useLanguage } from "../../../contexts/LanguageContext";
import { settingsLabels } from "../labels";
import { Building2, Users, Save } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";

interface NewRoomDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function NewRoomDrawer({ open, onOpenChange, onSuccess }: NewRoomDrawerProps) {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].rooms.newPage;
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);

    const trimmedName = name.trim();
    const capNumber = Number(capacity);
    if (!trimmedName || !Number.isFinite(capNumber) || capNumber <= 0) {
      setError(t.requiredError);
      return;
    }

    setSaving(true);
    try {
      const res = await api.createCompanyRoom({
        name: trimmedName,
        capacity: Math.max(1, Math.round(capNumber)),
      });
      if (!res.ok) {
        setError(res.error || t.requestError);
        return;
      }
      toast.success(t.success);
      setName("");
      setCapacity("");
      onOpenChange(false);
      onSuccess?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-0 rounded-[2.5rem] shadow-2xl p-0 overflow-hidden bg-white gap-0">
        <DialogHeader className="px-8 py-8 border-b border-neutral-50 bg-neutral-50/30 relative">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 mb-4 animate-in zoom-in-50 duration-500">
            <Building2 className="w-6 h-6" />
          </div>
          <DialogTitle className="text-xl font-bold text-neutral-800 tracking-tight">{t.title}</DialogTitle>
          <DialogDescription className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{t.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="room-name" className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 block">
                {t.fields.name}
              </Label>
              <div className="relative group">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 transition-colors group-focus-within:text-emerald-600" />
                <Input
                  id="room-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                  className="h-12 pl-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-medium text-neutral-800 focus:bg-white transition-all"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="room-capacity" className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 block">
                {t.fields.capacity}
              </Label>
              <div className="relative group">
                <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 transition-colors group-focus-within:text-emerald-600" />
                <Input
                  id="room-capacity"
                  value={capacity}
                  type="number"
                  min="1"
                  onChange={(e) => setCapacity(e.target.value)}
                  required
                  className="h-12 pl-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-medium text-neutral-800 focus:bg-white transition-all"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="p-4 rounded-2xl bg-destructive/5 border border-destructive/10 text-xs font-bold text-destructive uppercase tracking-widest text-center animate-in shake-1">
              {error}
            </div>
          )}

          <div className="pt-4 space-y-3">
            <Button 
              type="submit" 
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-[0.98]" 
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t.saving}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {t.save}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full h-12 font-bold text-neutral-400 rounded-2xl hover:bg-neutral-50 hover:text-neutral-600 transition-all"
              onClick={() => onOpenChange(false)}
            >
              {t.cancel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
